import { dbHelpers } from "@/lib/db";
import { useCallback, useEffect, useMemo, useState } from "react";

// EffectType and Effect can remain local if not shared, or be moved to a shared types file.
// For this task, keeping them local is fine as db.ts uses 'any' for settings value.
export type EffectType = "lowpass" | "highpass" | "reverb" | "distortion" | "compression" | "delay";

export type EqualizerSettings = {
  enabled: boolean;
  bands: number[];
};

export type Effect = {
  type: EffectType;
  enabled: boolean;
  value: number;
};

export interface AudioSettings {
  equalizer: EqualizerSettings;
  effects: Effect[];
  volume: number;
}

const defaultEqualizer: EqualizerSettings = {
  enabled: false,
  bands: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
};

const defaultEffects: Effect[] = [
  { type: "lowpass", enabled: false, value: 1 },
  { type: "highpass", enabled: false, value: 1 },
  { type: "reverb", enabled: false, value: 0.5 },
  { type: "distortion", enabled: false, value: 0.5 },
  { type: "compression", enabled: false, value: 0 },
  { type: "delay", enabled: false, value: 0.5 },
];

const defaultSettings = {
  equalizer: defaultEqualizer,
  effects: defaultEffects,
  volume: 0.8, // Default safe volume
};

export const useAudioSettings = (onSettingsChange: () => void) => {
  const [equalizer, setEqualizer] = useState<EqualizerSettings>(defaultEqualizer);
  const [effects, setEffects] = useState<Effect[]>(defaultEffects);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolume] = useState(defaultSettings.volume);
  const [migrationDone, setMigrationDone] = useState(false); // To ensure migration runs once

  const migrateOldAudioSettings = useCallback(async () => {
    try {
      const request = indexedDB.open("audioSettingsDB", 1);
      let dbDeleted = false;

      await new Promise<void>((resolve, reject) => {
        request.onsuccess = async (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains("audioSettings")) {
            console.log(
              "Old audioSettingsDB does not contain audioSettings store. No migration needed from this path."
            );
            db.close();
            // Attempt to delete DB even if store isn't there, in case it's an empty old DB
            try {
              await new Promise<void>((delResolve, delReject) => {
                const delRequest = indexedDB.deleteDatabase("audioSettingsDB");
                delRequest.onsuccess = () => {
                  console.log("Old audioSettingsDB (empty or no store) deleted successfully.");
                  delResolve();
                };
                delRequest.onerror = (e) => {
                  console.error("Error deleting empty/no-store audioSettingsDB", e);
                  delReject(delRequest.error);
                };
                delRequest.onblocked = () => {
                  console.warn("Old audioSettingsDB deletion blocked.");
                  delReject(new Error("Deletion blocked"));
                };
              });
            } catch (delError) {
              console.warn("Could not delete empty/no-store audioSettingsDB during check:", delError);
            }
            resolve();
            return;
          }

          console.log("Old audioSettingsDB found. Attempting migration.");
          const tx = db.transaction("audioSettings", "readonly");
          const store = tx.objectStore("audioSettings");

          const equalizerReq = store.get("equalizer");
          const effectsReq = store.get("effects");
          const volumeReq = store.get("volume");

          let oldEqualizer: EqualizerSettings | undefined;
          let oldEffects: Effect[] | undefined;
          let oldVolume: number | undefined;

          equalizerReq.onsuccess = () => (oldEqualizer = equalizerReq.result?.value);
          effectsReq.onsuccess = () => (oldEffects = effectsReq.result?.value);
          volumeReq.onsuccess = () => (oldVolume = volumeReq.result?.value);

          tx.oncomplete = async () => {
            db.close(); // Close the old DB before trying to delete it
            if (oldEqualizer || oldEffects || oldVolume !== undefined) {
              console.log("Migrating data:", { oldEqualizer, oldEffects, oldVolume });
              const mainSettings = (await dbHelpers.getSettings()) || {};

              if (oldEqualizer) mainSettings.equalizer = oldEqualizer;
              if (oldEffects) mainSettings.effects = oldEffects;
              if (oldVolume !== undefined) mainSettings.audioVolume = oldVolume;

              await dbHelpers.updateSettings(mainSettings);
              console.log("Old settings migrated to main DB.");

              // Delete old DB
              await new Promise<void>((delResolve, delReject) => {
                const delRequest = indexedDB.deleteDatabase("audioSettingsDB");
                delRequest.onsuccess = () => {
                  console.log("Old audioSettingsDB deleted successfully.");
                  dbDeleted = true;
                  delResolve();
                };
                delRequest.onerror = (e) => {
                  console.error("Error deleting old audioSettingsDB", e);
                  delReject(delRequest.error);
                };
                delRequest.onblocked = () => {
                  console.warn("Old audioSettingsDB deletion blocked. Please close other tabs/connections.");
                  // If blocked, we can't resolve, migration might re-run.
                  // This is an edge case. Usually, closing the DB above helps.
                  delReject(new Error("Deletion blocked"));
                };
              });
            } else {
              console.log("No data found in old audioSettingsDB to migrate.");
              // If no data, still try to delete the (potentially empty) old DB
              try {
                await new Promise<void>((delResolve, delReject) => {
                  const delRequest = indexedDB.deleteDatabase("audioSettingsDB");
                  delRequest.onsuccess = () => {
                    console.log("Old audioSettingsDB (empty) deleted successfully.");
                    delResolve();
                  };
                  delRequest.onerror = (e) => {
                    console.error("Error deleting empty audioSettingsDB", e);
                    delReject(delRequest.error);
                  };
                  delRequest.onblocked = () => {
                    console.warn("Old audioSettingsDB (empty) deletion blocked.");
                    delReject(new Error("Deletion blocked"));
                  };
                });
              } catch (delError) {
                console.warn("Could not delete empty audioSettingsDB after check:", delError);
              }
            }
            resolve();
          };
          tx.onerror = () => {
            db.close();
            reject(tx.error);
          };
        };
        request.onupgradeneeded = (event) => {
          // This implies the DB didn't exist or version was different.
          // For migration, we only care if it *did* exist with version 1 and the store.
          // If upgradeneeded is called, it means it's either a new DB or structure is unexpected.
          // We can let it proceed to onsuccess, which will then check for the store.
          // Or, if we are sure that an old DB would be version 1:
          console.log(
            "Old audioSettingsDB onupgradeneeded - likely doesn't exist or is new. No migration from this path."
          );
          // event.target.transaction.abort(); // Abort, no store to read from
          // resolve(); // Or let it complete and onsuccess will find no store.
        };
        request.onerror = (event) => {
          console.error("Error opening old audioSettingsDB for migration:", event);
          reject(request.error);
        };
        request.onblocked = (event) => {
          console.warn("Opening old audioSettingsDB for migration was blocked.", event);
          // Do not reject here, as it might resolve later.
          // If it stays blocked, the user might need to close other tabs.
        };
      });
    } catch (error) {
      console.error("Migration process failed:", error);
      // Don't set error state here, as it's a background process.
    } finally {
      setMigrationDone(true);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    if (!migrationDone) {
      // This check is to ensure migration is attempted before loading.
      // migrateOldAudioSettings sets migrationDone=true in its finally block.
      // This might lead to a slight delay on first load if migration is slow.
      console.log("Waiting for migration attempt to complete before loading settings...");
      // This is a simplified way; a more robust solution might involve a loading state.
      // For now, we rely on migrateOldAudioSettings being called in the useEffect.
      return;
    }
    try {
      const mainSettings = await dbHelpers.getSettings();
      if (mainSettings) {
        setEqualizer(mainSettings.equalizer || defaultEqualizer);
        setEffects(mainSettings.effects || defaultEffects);
        setVolume(mainSettings.audioVolume ?? defaultSettings.volume); // Use ?? for volume
        console.log("Loaded settings from main DB:", mainSettings);
      } else {
        setEqualizer(defaultEqualizer);
        setEffects(defaultEffects);
        setVolume(defaultSettings.volume);
        console.log("No settings found in main DB, using defaults.");
      }
    } catch (error) {
      console.error("Failed to load settings from main DB:", error);
      setError("Failed to load audio settings. Please try refreshing the page.");
      // Fallback to defaults in case of error
      setEqualizer(defaultEqualizer);
      setEffects(defaultEffects);
      setVolume(defaultSettings.volume);
    }
  }, [migrationDone]);

  const debouncedSaveSettings = useMemo(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    return async (eq: EqualizerSettings, fx: Effect[]) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        try {
          console.log("Debounced save: EQ:", eq, "FX:", fx);
          const mainSettings = (await dbHelpers.getSettings()) || {};
          mainSettings.equalizer = eq;
          mainSettings.effects = fx;
          await dbHelpers.updateSettings(mainSettings);
          onSettingsChange();
          console.log("Settings saved to main DB via debouncedSaveSettings");
        } catch (error) {
          console.error("Failed to save settings to main DB:", error);
          setError("Failed to save audio settings. Please try again.");
        }
      }, 300); // 300ms debounce
    };
  }, [onSettingsChange]);

  const saveVolume = useCallback(
    async (newVolume: number) => {
      try {
        const mainSettings = (await dbHelpers.getSettings()) || {};
        mainSettings.audioVolume = newVolume;
        await dbHelpers.updateSettings(mainSettings);
        setVolume(newVolume); // Update local state
        onSettingsChange(); // Notify listeners
        console.log("Volume saved to main DB:", newVolume);
      } catch (error) {
        console.error("Failed to save volume to main DB:", error);
      }
    },
    [onSettingsChange]
  );

  useEffect(() => {
    // Run migration first, then load settings.
    // migrationDone state will coordinate this with loadSettings.
    migrateOldAudioSettings();
  }, [migrateOldAudioSettings]);

  useEffect(() => {
    if (migrationDone) {
      // Only load settings after migration attempt
      loadSettings();
    }
  }, [loadSettings, migrationDone]);

  // No longer need direct saveSettings for equalizer/effects, handled by debouncedSaveSettings
  // useEffect(() => {
  //   saveSettings();
  // }, [equalizer, effects, saveSettings]);

  const updateEqualizer = useCallback(
    (newSettings: EqualizerSettings) => {
      setEqualizer(newSettings); // Update local state immediately for responsiveness
      debouncedSaveSettings(newSettings, effects); // Then save to DB
    },
    [effects, debouncedSaveSettings] // effects needs to be in dep array if used by debouncedSaveSettings context
  );

  const updateEffect = useCallback(
    (updatedEffect: Effect) => {
      const newEffects = effects.map((effect) => (effect.type === updatedEffect.type ? updatedEffect : effect));
      setEffects(newEffects); // Update local state immediately
      debouncedSaveSettings(equalizer, newEffects); // Then save to DB
    },
    [equalizer, effects, debouncedSaveSettings] // equalizer and effects for same reason
  );

  const presets = [
    { name: "Flat", bands: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    { name: "Bass Boost", bands: [7, 6, 5, 4, 3, 0, 0, 0, 0, 0] },
    { name: "Treble Boost", bands: [0, 0, 0, 0, 0, 3, 4, 5, 6, 7] },
    { name: "Vocal Boost", bands: [0, 0, 0, 2, 4, 4, 2, 0, 0, 0] },
    { name: "Rock", bands: [6, 4, 2, 0, 0, 0, 0, 2, 4, 6] },
    { name: "Pop", bands: [4, 2, 0, 0, 0, 0, 0, 2, 4, 6] },
    { name: "Jazz", bands: [3, 3, 0, 0, 0, 0, 0, 3, 3, 3] },
    { name: "Classical", bands: [2, 2, 2, 0, 0, 0, 0, 2, 2, 2] },
    { name: "Hip Hop", bands: [6, 5, 4, 3, 2, 0, 0, 0, 0, 0] },
    { name: "Techno", bands: [0, 0, 0, 0, 0, 2, 3, 4, 5, 6] },
  ];

  const setPreset = useCallback(
    (presetName: string) => {
      const preset = presets.find((p) => p.name === presetName);
      if (preset) {
        updateEqualizer({ ...equalizer, bands: preset.bands });
        console.log("Preset applied:", presetName);
      }
    },
    [equalizer, updateEqualizer]
  );

  return {
    equalizer,
    updateEqualizer,
    effects,
    updateEffect,
    presets,
    setPreset,
    error,
    volume,
    saveVolume,
  };
};

// Old openDatabase function is removed.
